import { FullScreenLoading, ProjectCardPopover } from '@civilgrid/ui/components';
import type { FilterGroupItem } from '@civilgrid/ui/utils';
import { cn, getNonNullUserName } from '@civilgrid/ui/utils';
import { useRouterState } from '@tanstack/react-router';
import { bbox } from '@turf/turf';
import type { FeatureCollection, MultiLineString, MultiPolygon, Polygon } from 'geojson';
import { isEmpty, toNumber } from 'lodash';
import { type ComponentProps, useCallback, useEffect, useMemo, useRef } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { getProjectCenter } from '~/common/projectUtils';
import { useQuery as useRouterQuery } from '~/common/routerUtils';
import { ProjectGeometryType } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import { MapComponent as MapboxMap } from '~/components2/Map';
import type { MapProps } from '~/components2/Map/common';
import { getOrgStateInitialViewport } from '~/components2/Map/helpers/defaultInitialViewport';
import {
  consumeReturnViewport,
  historyEntryKeySelector,
  layerLibraryReturnKey,
  peekReturnViewport,
} from '~/components2/Map/helpers/layerLibraryReturnViewport';
import DelegatedAccessProjects from '~/components2/Map/Widgets/DelegatedAccessProjects';
import { onRootBasemapChange } from '~/pages/common/helpers';
import NewProject from '~/pages/common/NewProject';
import rootStore from '~/store';
import { useGetAllProjectsForMap, useToggleProjectBookmark } from '../queries';
import { homeMapStore, homeViewStore } from '../store';
import type { ProjectViewMode } from '../types';
import { formatAddress } from '../utils';
import { AddressSearchResult } from './AddressSearchResult';
import AgenciesSummary from './AgenciesSummary';
import DrawSurface from './DrawSurface';
import ProjectPolygons from './ProjectPolygons';
import Surface from './Surface';

type MapViewProps = { viewMode: ProjectViewMode };

export default function MapView({ viewMode }: MapViewProps) {
  const { inUnverifiedMode, currentOrganizationId, currentOrganizationDetail } = useSnapshot(
    rootStore,
  ) as typeof rootStore;
  const { inNewProjectMode, popupParams, flyToProject } = useSnapshot(homeMapStore);
  const { filters, globalFilter, bookmarksOnly } = useSnapshot(homeViewStore);
  const { homeMap } = MapGL.useMap();
  const routerQuery = useRouterQuery();
  const previousOrganizationIdRef = useRef(currentOrganizationId);
  // CG-326: deep-link wins for this mount; the deep-link effect mutates routerQuery later,
  // so snapshot presence now to keep state-zoom from clobbering it on a subsequent rerun.
  const deepLinkAtMountRef = useRef(
    !isEmpty(routerQuery.get('lng')) && !isEmpty(routerQuery.get('lat')) && !isEmpty(routerQuery.get('z')),
  );

  // Camera stashed by the Layer Library round-trip; reused map instances ignore
  // `initialViewState`, so the effect also applies it via jumpTo.
  const historyKey = useRouterState({ select: historyEntryKeySelector });
  const restoredInitialViewState = useMemo(() => peekReturnViewport(layerLibraryReturnKey(), historyKey), [historyKey]);
  const pendingReturnRestoreRef = useRef<MapGL.ViewState | null>(null);
  const hasRestoredReturnViewportRef = useRef(false);

  useEffect(() => {
    const stashed = consumeReturnViewport(layerLibraryReturnKey(), historyKey);
    if (stashed) {
      pendingReturnRestoreRef.current = stashed;
      hasRestoredReturnViewportRef.current = true;
    }
    const restore = pendingReturnRestoreRef.current;
    if (!restore || !homeMap) return;
    homeMap.jumpTo({
      center: [restore.longitude, restore.latitude],
      zoom: restore.zoom,
      bearing: restore.bearing,
      pitch: restore.pitch,
      padding: restore.padding,
    });
    pendingReturnRestoreRef.current = null;
  }, [homeMap, historyKey]);

  // const orgProjectBounds =
  //   currentOrganizationDetail.isLoading === false
  //     ? (currentOrganizationDetail as OrganizationDetail).projectBounds
  //     : null;

  // When the organization changes, clear the initial fit bounds so the map refits to the new org.
  // If within the same org, do not run (does not clear fit bounds when basemap changes)
  useEffect(() => {
    const prev = previousOrganizationIdRef.current;
    if (prev !== currentOrganizationId && prev != null) {
      homeMapStore.setHasInitialFitBounds(false);
    }
    previousOrganizationIdRef.current = currentOrganizationId;
  }, [currentOrganizationId]);

  // useEffect(() => {
  //   if (!currentOrganizationId) return;
  //   if (homeMapStore.hasInitialFitBounds) return;
  //   if (location.state && (location.state as Record<string, unknown>)['from']) return;
  //   if (!orgProjectBounds) return;
  //   // Map mounts only after projects query finishes (FullScreenLoading); skip until instance exists.
  //   if (!homeMap) return;
  //
  //   const [west, south, east, north] = bbox(orgProjectBounds);
  //   homeMap.fitBounds([west, south, east, north] as [number, number, number, number], {
  //     padding: 100,
  //   });
  //   homeMapStore.setHasInitialFitBounds(true);
  // }, [homeMap, orgProjectBounds, currentOrganizationId, location.state]);

  // Map view = all projects; Split view = bookmarkedOnly when bookmarks filter is selected
  const allProjectsQuery = useGetAllProjectsForMap({
    organizationId: currentOrganizationId ?? '',
    filters: filters as FilterGroupItem,
    globalFilter,
    bookmarkedOnly: viewMode === 'split' ? bookmarksOnly : false,
    enabled: (viewMode === 'map' || viewMode === 'split') && !!currentOrganizationId,
  });

  const isLoading = allProjectsQuery.isLoading === true;
  const projects = allProjectsQuery.data?.projects ?? [];
  const currentOrganizationState = currentOrganizationDetail.isLoading ? null : currentOrganizationDetail.state;
  const toggleBookmark = useToggleProjectBookmark();
  const handleBookmarkClick = useCallback(
    (projectId: string) => () => {
      toggleBookmark.mutate(projectId);
    },
    [toggleBookmark],
  );

  const projectPolygons = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: isLoading
        ? []
        : projects.map((p) => ({
            type: 'Feature',
            geometry: p.geometry,
            properties: {
              id: p.id,
              projectId: p.projectId,
              projectName: p.name,
              projectManager: getNonNullUserName(p.projectManager?.name) || p.projectManager?.email || '',
              projectPhase: p.projectPhase,
              projectYear: p.projectYear,
              sandboxProject: p.sandboxProject,
              projectBillingTier: p.billingTier,
              projectAddress: formatAddress(p.address2),
              ...(p.geometryType === ProjectGeometryType.Circle &&
                !!p.circle && {
                  circleCenter: p.circle.geometry,
                }),
            },
          })),
    } as FeatureCollection<Polygon | MultiPolygon>;
  }, [isLoading, projects]);

  useEffect(() => {
    const lng = routerQuery.get('lng');
    const lat = routerQuery.get('lat');
    const z = routerQuery.get('z');

    if (!isEmpty(lng) && !isEmpty(lat) && !isEmpty(z)) {
      setTimeout(() => {
        homeMap?.flyTo({
          center: [toNumber(lng), toNumber(lat)],
          zoom: toNumber(z),
        });

        routerQuery.delete('lng');
        routerQuery.delete('lat');
        routerQuery.delete('z');
      }, 1_000);
    }
  }, [routerQuery, homeMap]);

  // CG-326: apply org-state viewport only when there are no projects; with projects, cluster auto-fit owns init.
  useEffect(() => {
    if (!homeMap) return;
    if (projects.length > 0) return;
    // A camera restored from the Layer Library round-trip owns this mount's viewport.
    if (hasRestoredReturnViewportRef.current) return;
    if (currentOrganizationDetail.isLoading) return;
    if (!currentOrganizationId) return;

    const viewport = getOrgStateInitialViewport();
    if (!viewport) return;

    homeMapStore.setHasInitialFitBounds(true);

    if (deepLinkAtMountRef.current) return;

    homeMap.flyTo({
      center: [viewport.longitude, viewport.latitude],
      zoom: viewport.zoom,
    });

    // Once per session per org.
    const flagKey = `cg326.stateZoomApplied.${currentOrganizationId}`;
    if (!window.sessionStorage.getItem(flagKey)) {
      window.sessionStorage.setItem(flagKey, '1');
      capturePosthogEvent(POSTHOG_EVENTS.stateZoomApplied, {
        state_code: currentOrganizationState?.trim().toUpperCase(),
        source: 'org',
        map_id: 'homeMap',
      });
    }
  }, [homeMap, currentOrganizationId, currentOrganizationDetail.isLoading, currentOrganizationState, projects.length]);

  // Resize map when container dimensions change (e.g. viewMode map <-> split).
  // biome-ignore lint/correctness/useExhaustiveDependencies: layout/size depends on viewMode and new-project panel
  useEffect(() => {
    if (!homeMap) return;
    const id = requestAnimationFrame(() => {
      homeMap.resize();
    });
    return () => cancelAnimationFrame(id);
  }, [viewMode, inNewProjectMode, homeMap]);

  // When table/list requests "show on map", fit bounds to project geometry and show popup at centroid.
  useEffect(() => {
    if (!flyToProject || !homeMap || !flyToProject.geometry) return;
    const p = flyToProject;
    const geom = p.geometry as MultiPolygon | MultiLineString;
    homeMapStore.setNewProjectMode(false);
    homeMap.fitBounds(bbox(geom) as [number, number, number, number], {
      padding: 100,
      speed: 2,
    });

    homeMapStore.setPopupParams({
      lngLat: getProjectCenter(geom),
      id: p.id,
      projectId: p.projectId,
      projectName: p.name ?? '',
      projectManager: getNonNullUserName(p.projectManager?.name) || p.projectManager?.email || '',
      projectPhase: p.projectPhase ?? '',
      projectYear: p.projectYear ?? '',
      projectBillingTier: p.billingTier ?? 'draft',
      projectAddress: formatAddress(p.address2),
    });
    homeMapStore.setFlyToProject(null);
  }, [flyToProject, homeMap]);

  const popup = useMemo(() => {
    if (popupParams) {
      // Bookmark state from projects query so it updates after toggle (projects refetch, not me)
      const popupProject = projects.find((p) => p.id === popupParams.id);

      const projectCardPopoverProps: ComponentProps<typeof ProjectCardPopover> = {
        billingTier: popupParams.projectBillingTier,
        name: popupParams.projectName,
        pmName: popupParams.projectManager,
        projectId: popupParams.projectId,
        address: popupParams.projectAddress,
        linkProps: { to: `/projects/${popupParams.id}`, openInNewTab: popupParams.isCrossOrgProject },
        hasCloseButton: true,

        ...(popupParams.isCrossOrgProject
          ? {
              organizationName: popupParams.organizationName,
            }
          : {
              bookmarked: popupProject?.bookmarked,
              onBookmarkClick: handleBookmarkClick(popupParams.id),
            }),
      };

      return (
        <MapGL.Popup
          key={`${popupParams.lngLat[0]},${popupParams.lngLat[1]}`}
          longitude={popupParams.lngLat[0]}
          latitude={popupParams.lngLat[1]}
          closeButton={false}
          closeOnMove={false}
          focusAfterOpen={false}
          maxWidth="auto"
          onClose={() => {
            homeMapStore.setPopupParams(undefined);
          }}
        >
          <div className="relative p-1">
            <div className="absolute right-0 top-0 z-10">
              <CloseIconButton
                aria-label="Close project card"
                onClick={() => homeMapStore.setPopupParams(undefined)}
                data-testid="home-map-popup-close-button"
              />
            </div>
            <ProjectCardPopover {...projectCardPopoverProps} />
          </div>
        </MapGL.Popup>
      );
    } else {
      return null;
    }
  }, [popupParams, projects, handleBookmarkClick]);

  if (isLoading) {
    return <FullScreenLoading />;
  }

  const mapProps: MapProps = {
    id: 'homeMap',
    store: homeMapStore,
    initialViewState: restoredInitialViewState,
    showFilterTableToMap: viewMode === 'split' || viewMode === 'map',
    mapStyle: rootStore.preferredBasemap,
    showLayers: true,
    showLayerPicker: true,
    showLegend: true,
    showBasemapPicker: true,
    onBasemapChange: onRootBasemapChange,
    showZoomButtons: true,
    showScale: true,
    allowHistoricalImagery: true,
    interactiveLayerIds: ['project-polygon'],
    isOutOfRegionDisabled: false,
    measureTool: true,
    printTool: true,
    allowRotation: true,
    projectPolygons: projectPolygons,
    allowSplitView: false,
  };

  return (
    <div className={cn('flex h-full w-full', inNewProjectMode ? 'pr-0' : 'pr-3')}>
      <div id="map-container" className="border-grey-30 flex h-full w-full rounded-lg border bg-white">
        {/* {inOnboardingMode && <HomeOnboarding />} */}
        <MapboxMap {...mapProps}>
          {popup}
          <ProjectPolygons polygons={projectPolygons} />
          <DelegatedAccessProjects />
          {/* {currentOrganizationId ? (
            <ProjectsLayer organizationId={currentOrganizationId} onProjectClick={onProjectClick} />
          ) : null} */}
          <Surface />
          <DrawSurface />
          <AddressSearchResult />
          {!inUnverifiedMode && !inNewProjectMode && <AgenciesSummary />}
        </MapboxMap>
        {inNewProjectMode && <NewProject />}
      </div>
    </div>
  );
}
